import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStyles } from 'src/components/CrosstableContent/components/AppartmentSidebarCross/styles';
import { useDispatch, useSelector } from 'react-redux';
import { requestSelectors } from 'src/store/requests/selectors';
import {
  getComplexId, getCrmData, getIsOpenAppartSidebar, getSelectedAppartment,
} from 'src/store/app/selectors';
import { useBuildType, useIsMobile } from 'src/hooks';
import { useTranslations } from 'src/hooks/translataions';
import { locals } from 'src/constants/locals';
import { ButtonApp } from 'src/components/Button';
import { CRMNames } from 'src/typings/app';
import { LayoutApartmentItem } from 'src/components/LayoutApartmentItem';
import {
  ApartmentSidebarActions,
} from 'src/components/CrosstableContent/components/AppartmentSidebarCross/components/ApartmentSidebarActions';
import { ApartmentConnectDeal } from 'src/components/ApartmentConnectDeal';
import { TypesOfSpinner } from 'src/typings/types-of-spinner';
import { getSelectedView } from 'src/store/type-displayed-spinner/selectors';
import { ApartmentStatus2dView } from 'src/components/LayoutApartmentItem/components/ApartmentStatus2dView';
import Box from '@mui/material/Box';
import {
  ApartmentSkeleton,
} from 'src/components/CrosstableContent/components/AppartmentSidebarCross/components/ApartmentSkeleton';
import {
  useCrmBitrixDeal,
} from 'src/components/CrosstableContent/components/AppartmentSidebarCross/hooks/use-placement-deals';
import useSwipe from 'src/hooks/useSwipeGesture';
import { getFilterInitialData } from 'src/store/requests/filter/selector';
import { getSelectedPlacement } from 'src/store/requests/type-of-property/selector';
import { Actions } from 'src/store/app/actions';
import { Actions as FloorsActions } from 'src/store/floors/actions';

interface Props {
  showDealDetails: boolean
}
export const ApartmentSidebarCross: React.FC<Props> = ({
  showDealDetails,
}) => {
  const selectedApartmentId = useSelector(getSelectedAppartment);
  const complexIdState = useSelector(getComplexId);
  const { getTranslations } = useTranslations();
  const {
    placementDeal, isLoading, setIsLoading, getPlacementDeals, setPlacementDeal,
  } = useCrmBitrixDeal();
  const filteredData = useSelector(getFilterInitialData);
  const mainColor = useSelector(requestSelectors.app.getMainColors);
  const isOpenApartSidebar = useSelector(getIsOpenAppartSidebar);
  const dispatch = useDispatch();
  const crmData = useSelector(getCrmData);
  const { isCrossTable } = useBuildType();
  const { isMobileScreen } = useIsMobile();
  const getSelectedViewId = useSelector(getSelectedView);
  const isView2d = getSelectedViewId === TypesOfSpinner.view2d;
  const swipeHandlers = useSwipe({ onSwipedRight: () => handleCloseSidebar() });
  const styles = useStyles(mainColor);
  const typeOfProperty: any = useSelector(getSelectedPlacement);

  useEffect(() => {
    // Skeleton while the deal info of the newly selected unit loads (the hook
    // flips isLoading around the fetch). Link/unlink loading is reported
    // separately by ApartmentConnectDeal via onLoading — it can't own this
    // fetch: while the skeleton is up the deal block is unmounted, so a
    // mount-effect fetch would loop.
    // Same gate as ApartmentConnectDeal: no crmData means we are not in CRM
    // mode, and the bitrix placements endpoint is only relevant for bitrix.
    if (crmData?.crm === CRMNames.bitrix && selectedApartmentId) {
      getPlacementDeals(selectedApartmentId);
    }
  }, [selectedApartmentId, crmData]);

  if (!selectedApartmentId || !filteredData || !filteredData[typeOfProperty]?.filter_all_data?.[selectedApartmentId]) {
    return null;
  }
  const { filter_all_data: filterAllData } = filteredData[typeOfProperty];

  const handleCloseSidebar = () => {
    dispatch(Actions.setIsOpenAppartSidebar(false));
    dispatch(FloorsActions.setSelectedApartmentId(undefined));
  };

  const onLoading = (value: boolean) => {
    setIsLoading(value);
  };

  return (
    <div
      data-apartment-sidebar
      className={`${styles.sidebarWrapper} ${
        isOpenApartSidebar ? styles.wrapperOpen : styles.wrapperClose
      }`}
      {...swipeHandlers}
    >
      {!isLoading && (
        <>
          { isCrossTable && isView2d && !isLoading && (
            <ApartmentStatus2dView property={filterAllData[selectedApartmentId]} onCloseClick={handleCloseSidebar} />
          )}
          <Box>
            <ApartmentSidebarActions placement={filterAllData[selectedApartmentId]} />
            <Box padding="16px" display="flex" gap="16px" flexDirection="column">
              <>
                <LayoutApartmentItem
                  withSizeLimits={isMobileScreen}
                  wrapperStyle={styles.wrapperLayoutStyles}
                  property={filterAllData[selectedApartmentId]}
                  isShowFavIcon={false}
                />
                {!crmData?.dealId && (
                  <Link
                    to={
                      isCrossTable
                        ? `/${complexIdState}/own/${filterAllData[selectedApartmentId].id}`
                        : `/own/${filterAllData[selectedApartmentId].id}`
                    }
                    onClick={handleCloseSidebar}
                  >
                    <ButtonApp
                      fullWidth
                      text={getTranslations(locals.appTranslations.appMore)}
                      variant="contained"
                    />
                  </Link>
                ) }
                {isOpenApartSidebar && (
                  <ApartmentConnectDeal
                    placeInfo={filterAllData[selectedApartmentId]}
                    isSmall
                    showDealDetails={showDealDetails}
                    onLoading={onLoading}
                    placementDeal={placementDeal}
                    refreshPlacementDeal={getPlacementDeals}
                    updatePlacementDeal={setPlacementDeal}
                  />
                ) }
              </>
            </Box>
          </Box>
        </>
      )}
      {isLoading && (
        <Box padding="16px">
          <ApartmentSkeleton />
        </Box>
      )}
    </div>
  );
};
